// ═══════════════════════════════════════════════════════════════════════════════
// ZignaGenix Modern Design System — Shared Inline Style Constants
// ═══════════════════════════════════════════════════════════════════════════════
// Mirror of CSS design tokens for use in inline-styled React components.
// Usage: import { NEU } from '../neuStyles';  or  import { NEU } from './neuStyles';

const BG = '#F5F6FA';
const BG_CARD = '#FFFFFF';
const BORDER = '#E8ECF1';
const TEAL = '#2E8B98';
const TEAL_LIGHT = '#3CA0AE';
const TEAL_DARK = '#256F7A';
const RADIUS = 12;
const RADIUS_SM = 8;
const RADIUS_LG = 16;

export const NEU = {
  // ── Shadows — Modern Flat Style ────────────────────────────────────────────
  shadow: {
    xs:      `0 1px 2px rgba(0,0,0,0.05)`,
    sm:      `0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)`,
    md:      `0 4px 12px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)`,
    lg:      `0 10px 20px rgba(0,0,0,0.08), 0 4px 8px rgba(0,0,0,0.04)`,
    inset:   `inset 0 1px 2px rgba(0,0,0,0.04)`,
    insetSm: `inset 0 1px 2px rgba(0,0,0,0.04)`,
    pressed: `inset 0 2px 4px rgba(0,0,0,0.06)`,
    pill:    `0 1px 2px rgba(0,0,0,0.05)`,
  },

  // ── Colors ─────────────────────────────────────────────────────────────────
  bg:         BG,
  bgCard:     BG_CARD,
  border:     BORDER,
  teal:       TEAL,
  tealLight:  TEAL_LIGHT,
  tealDark:   TEAL_DARK,

  // ── Radii ──────────────────────────────────────────────────────────────────
  radius:    RADIUS,
  radiusSm:  RADIUS_SM,
  radiusLg:  RADIUS_LG,

  // ── Composite Styles (drop-in replacements) ────────────────────────────────

  /** Card / panel — white background with border and subtle shadow */
  card: {
    background: BG_CARD,
    border: `1px solid ${BORDER}`,
    borderRadius: RADIUS,
    boxShadow: `0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)`,
  },

  /** Smaller card — stat cards, list items */
  cardSm: {
    background: BG_CARD,
    border: `1px solid ${BORDER}`,
    borderRadius: RADIUS_SM,
    boxShadow: `0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)`,
  },

  /** Large container card */
  cardLg: {
    background: BG_CARD,
    border: `1px solid ${BORDER}`,
    borderRadius: RADIUS_LG,
    boxShadow: `0 4px 12px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)`,
  },

  /** Inset / recessed panel — for search bars, filter areas, detail blocks */
  inset: {
    background: BG,
    border: `1px solid ${BORDER}`,
    borderRadius: RADIUS_SM,
    boxShadow: `inset 0 1px 2px rgba(0,0,0,0.04)`,
  },

  /** Form input */
  input: {
    background: BG_CARD,
    border: `1px solid ${BORDER}`,
    borderRadius: RADIUS_SM,
    boxShadow: `none`,
    outline: 'none',
    fontFamily: 'inherit',
    padding: '8px 12px',
  },

  /** Primary button (solid teal, neumorphic) */
  btnPrimary: {
    background: 'linear-gradient(145deg, #3CA0AE, #256F7A)',
    color: '#fff',
    border: 'none',
    borderRadius: RADIUS_SM,
    cursor: 'pointer',
    fontWeight: 600,
    boxShadow: `5px 5px 10px rgba(174,174,192,0.5), -5px -5px 10px rgba(255,255,255,0.9),
                inset 0 2px 0 rgba(255,255,255,0.2), inset 0 -2px 0 rgba(0,0,0,0.1)`,
    padding: '8px 16px',
    position: 'relative',
    top: 0,
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  /** Ghost / secondary button (neumorphic) */
  btnGhost: {
    background: BG,
    color: '#64748B',
    border: 'none',
    borderRadius: RADIUS_SM,
    cursor: 'pointer',
    fontWeight: 500,
    boxShadow: `4px 4px 8px rgba(174,174,192,0.4), -4px -4px 8px rgba(255,255,255,0.8),
                inset 0 1px 0 rgba(255,255,255,0.4)`,
    padding: '8px 16px',
    position: 'relative',
    top: 0,
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  /** Danger / red button (neumorphic) */
  btnDanger: {
    background: 'linear-gradient(145deg, #E4606D, #B02A37)',
    color: '#fff',
    border: 'none',
    borderRadius: RADIUS_SM,
    cursor: 'pointer',
    fontWeight: 600,
    boxShadow: `5px 5px 10px rgba(174,174,192,0.5), -5px -5px 10px rgba(255,255,255,0.9),
                inset 0 2px 0 rgba(255,255,255,0.2), inset 0 -2px 0 rgba(0,0,0,0.1)`,
    padding: '8px 16px',
    position: 'relative',
    top: 0,
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  /** Tab — inactive (neumorphic) */
  tab: {
    background: BG,
    border: 'none',
    borderRadius: RADIUS_SM,
    cursor: 'pointer',
    boxShadow: `3px 3px 6px rgba(174,174,192,0.4), -3px -3px 6px rgba(255,255,255,0.8),
                inset 0 1px 0 rgba(255,255,255,0.4)`,
    fontWeight: 500,
    padding: '8px 16px',
    position: 'relative',
    top: 0,
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  /** Tab — active (pressed, neumorphic inset) */
  tabActive: {
    background: TEAL,
    color: '#fff',
    border: 'none',
    borderRadius: RADIUS_SM,
    cursor: 'pointer',
    boxShadow: `inset 4px 4px 8px rgba(0,0,0,0.2), inset -3px -3px 6px rgba(255,255,255,0.1)`,
    fontWeight: 600,
    padding: '8px 16px',
    position: 'relative',
    top: 0,
  },

  /** Badge / pill */
  badge: {
    display: 'inline-block',
    borderRadius: 99,
    fontWeight: 600,
    fontSize: 12,
    boxShadow: `none`,
    padding: '4px 12px',
    border: `1px solid ${BORDER}`,
  },

  /** Table container */
  tableWrap: {
    background: BG_CARD,
    border: `1px solid ${BORDER}`,
    borderRadius: RADIUS,
    overflow: 'hidden',
    boxShadow: `0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)`,
  },

  /** Table header cell */
  th: {
    background: BG,
    borderBottom: `1px solid ${BORDER}`,
    boxShadow: `none`,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontSize: 11,
    color: '#94A3B8',
    padding: '12px 16px',
  },

  /** Table body cell */
  td: {
    borderBottom: `1px solid ${BORDER}`,
    boxShadow: `none`,
    padding: '12px 16px',
  },

  /** Table row hover */
  trHover: {
    background: BG,
    boxShadow: `none`,
  },

  /** Section divider (instead of border-top/bottom) */
  dividerTop: {
    borderTop: `1px solid ${BORDER}`,
    boxShadow: `none`,
  },

  /** Modal overlay */
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(2px)',
  },

  /** Modal card */
  modal: {
    background: BG_CARD,
    borderRadius: RADIUS_LG,
    boxShadow: `0 15px 30px rgba(0,0,0,0.1), 0 6px 12px rgba(0,0,0,0.05)`,
    maxHeight: '85vh',
    overflow: 'auto',
    border: `1px solid ${BORDER}`,
  },

  /** Stat icon circle */
  statIcon: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `none`,
    background: `rgba(46, 139, 152, 0.1)`,
  },
};

export default NEU;
